import { useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table';
import { TooltipProvider } from '../../ui/tooltip';
import { MoMCell, formatNumber, formatCurrency, calculateMoM } from '../../dashboard';
import { getCurrentMonth, getCurrentMonthProgress } from '../../../utils/dateUtils';
import { MonthlyRow } from '../../../services/api/CPTAnalytics/CPTActivityService';
import { InfoTip } from './InfoTip';

interface CPTMonthlyTableProps {
    rows: MonthlyRow[];
}


const calculatePacingProjection = (
    month: string,
    estimatedRevenue: number
): number | undefined => {
    if (month !== getCurrentMonth()) return undefined;

    const { dayOfMonth, daysInMonth } = getCurrentMonthProgress();
    if (dayOfMonth === 0) return undefined;
    return (estimatedRevenue / dayOfMonth) * daysInMonth;
};

export default function CPTMonthlyTable({
    rows: rawRows,
}: CPTMonthlyTableProps) {
    const rows = Array.isArray(rawRows) ? rawRows : [];

    const enrichedRows = useMemo(() => {
        return rows.map((row, index) => {
            const prev = index > 0 ? rows[index - 1] : null;
            return {
                ...row,
                rvuMoMPercent: calculateMoM(
                    row.totalRVU,
                    prev?.totalRVU ?? null
                ),
                revenueMoMPercent: calculateMoM(
                    row.estimatedRevenue,
                    prev?.estimatedRevenue ?? null
                ),
                pacingProjection: calculatePacingProjection(
                    row.month,
                    row.estimatedRevenue
                ),
            };
        });
    }, [rows]);

    if (enrichedRows.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-subtle py-12 text-center">
                <p className="text-sm text-muted-foreground">
                    No monthly data available.
                </p>
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <div className="overflow-auto rounded-lg border border-subtle">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">
                                Total Qty
                            </TableHead>
                            <TableHead className="text-right">
                                Total RVU
                            </TableHead>
                            <TableHead className="text-right">
                                Est. Revenue
                            </TableHead>
                            <TableHead className="text-right">
                                <span className="inline-flex items-center gap-1.5">
                                    Pacing Projection
                                    <InfoTip id="monthly-pacing-projection" text="Estimated month-end revenue based on the current daily average." />
                                </span>
                            </TableHead>
                            <TableHead>
                                <span className="inline-flex items-center gap-1.5">
                                    RVU <span className="normal-case">MoM</span> %
                                    <InfoTip id="monthly-rvu-mom" text="Month-over-month change — how this month compares to the previous month." />
                                </span>
                            </TableHead>
                            <TableHead>
                                <span className="inline-flex items-center gap-1.5">
                                    Revenue <span className="normal-case">MoM</span> %
                                    <InfoTip id="monthly-revenue-mom" text="Month-over-month change in estimated revenue." />
                                </span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {enrichedRows.map((row) => (
                            <TableRow key={row.month}>
                                <TableCell className="font-medium">
                                    {row.month}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {row.totalQuantity}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatNumber(row.totalRVU)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatCurrency(row.estimatedRevenue)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {row.pacingProjection !== undefined
                                        ? formatCurrency(row.pacingProjection)
                                        : '—'}
                                </TableCell>
                                <TableCell>
                                    <MoMCell value={row.rvuMoMPercent} />
                                </TableCell>
                                <TableCell>
                                    <MoMCell value={row.revenueMoMPercent} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </TooltipProvider>
    );
}
